import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { useInvestmentProducts } from '../../hooks/useInvestments';
import type { InvestmentProductType, RiskRating, RateType, LiquidityType } from '../../types/investments';

const typeLabels: Record<InvestmentProductType, string> = {
  CDB: 'CDB',
  LCI: 'LCI',
  LCA: 'LCA',
  FUND: 'Fundo',
  DEBENTURE: 'Debênture',
  CRI: 'CRI',
  CRA: 'CRA',
};

const riskColors: Record<RiskRating, 'success' | 'primary' | 'default' | 'error'> = {
  VERY_LOW: 'success',
  LOW: 'success',
  MEDIUM: 'primary',
  HIGH: 'error',
  VERY_HIGH: 'error',
};

const riskLabels: Record<RiskRating, string> = {
  VERY_LOW: 'Muito Baixo',
  LOW: 'Baixo',
  MEDIUM: 'Médio',
  HIGH: 'Alto',
  VERY_HIGH: 'Muito Alto',
};

const liquidityLabels: Record<LiquidityType, string> = {
  DAILY: 'Liquidez Diária',
  AT_MATURITY: 'No Vencimento',
  GRACE_PERIOD: 'Após Carência',
};

export function InvestmentMarketplace() {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<InvestmentProductType | undefined>();
  const [riskFilter, setRiskFilter] = useState<RiskRating | undefined>();
  const { data: productsData, isLoading } = useInvestmentProducts({ 
    type: typeFilter, 
    riskRating: riskFilter,
    isAvailable: true 
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatRate = (product: { rateType: RateType; rate: number }) => {
    switch (product.rateType) {
      case 'FIXED':
        return `${product.rate.toFixed(2)}% a.a.`;
      case 'CDI_PERCENTAGE':
        return `${product.rate.toFixed(0)}% do CDI`;
      case 'CDI_PLUS':
        return `CDI + ${product.rate.toFixed(2)}%`;
      case 'IPCA_PLUS':
        return `IPCA + ${product.rate.toFixed(2)}%`;
      default:
        return `${product.rate}%`;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Investimentos</h1>
          <p className="text-gray-500">Encontre as melhores opções para seu perfil</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/banking/investments/portfolio')}>
            Meu Portfólio
          </Button>
          <Button variant="secondary" onClick={() => navigate('/banking/investments/profile')}>
            Meu Perfil
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Tipo de Produto</label>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={!typeFilter ? 'primary' : 'secondary'}
                  onClick={() => setTypeFilter(undefined)}
                >
                  Todos
                </Button>
                {Object.entries(typeLabels).map(([type, label]) => (
                  <Button
                    key={type}
                    size="sm"
                    variant={typeFilter === type ? 'primary' : 'secondary'}
                    onClick={() => setTypeFilter(type as InvestmentProductType)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Nível de Risco</label>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={!riskFilter ? 'primary' : 'secondary'}
                  onClick={() => setRiskFilter(undefined)}
                >
                  Todos
                </Button>
                {(['VERY_LOW', 'LOW', 'MEDIUM', 'HIGH'] as RiskRating[]).map((risk) => (
                  <Button
                    key={risk}
                    size="sm"
                    variant={riskFilter === risk ? 'primary' : 'secondary'}
                    onClick={() => setRiskFilter(risk)}
                  >
                    {riskLabels[risk]}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products Grid */}
      {productsData?.products && productsData.products.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {productsData.products.map((product) => (
            <Card key={product.id} className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/banking/investments/${product.id}`)}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="default">{typeLabels[product.type]}</Badge>
                    {product.taxExempt && (
                      <Badge variant="success" className="ml-2">Isento IR</Badge>
                    )}
                  </div>
                  <Badge variant={riskColors[product.riskRating]}>
                    {riskLabels[product.riskRating]}
                  </Badge>
                </div>
                <CardTitle className="text-lg mt-2">{product.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-center py-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {formatRate(product)}
                    </p>
                    <p className="text-sm text-gray-500">Rentabilidade</p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500">Mínimo</p>
                      <p className="font-medium">{formatCurrency(product.minimumAmount)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Liquidez</p>
                      <p className="font-medium">{liquidityLabels[product.liquidityType]}</p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-500">{product.issuer}</p>

                  <Button className="w-full" size="sm">
                    Ver Detalhes
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Nenhum produto encontrado com os filtros selecionados</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default InvestmentMarketplace;
